import type { Route, Routes } from '@angular/router';
import { APP_PATHS, MAIN_LANG, TransactionStatus } from '@dev-fast/types';

import { IsAuthGuard } from '@app/auth';
import { AutoGameActivateChildGuard, WelcomeWorkActivateGuard, WelcomeWorkDeactivateGuard } from '@app/core/guards';
import { LanguageService } from '@app/core/language-service';
import { PromocodeService } from '@app/core/promocode-service';
import { GofastLayoutComponent } from '@app/gofast/layout-new';
import { ChatService } from '@app/gofast/layout-new/lib/services/chat.service';
import { HasPermissionService } from '@app/gofast/layout-new/lib/services/has-permission.service';
import { MainModalsService } from '@app/gofast/layout-new/lib/services/main-modals.service';
import { ParticipationPanelService } from '@app/gofast/layout-new/lib/services/participation-panel.service';
import { InventoryEngineService } from '@app/gofast/layout-new-market/lib/services/inventory-engine.service';
import { REFILL_BANNER_ENGINE } from '@app/shared/components';
import { INVENTORY_TOOLTIP_ENGINE } from '@app/shared/components/inventory-tooltip/symbols';
import { RefillBannerService } from '@app/shared/components/refill-banner/refill-banner.service';
import { HAS_PERMISSION_ENGINE } from '@app/shared/directives/has-permission/symbols';
import { CHAT_ENGINE } from '@app/widgets/chat-v2';
import { PARTICIPATION_PANEL_ENGINE } from '@app/widgets/participation-panel';
import { PROMOCODE_ENGINE } from '@app/widgets/promocode';
import { RECENT_ACTIVITIES_ENGINE, RecentActivitiesService } from '@app/widgets/recent-activities';

import { appRoutes } from './app.routes';
import { footerRoutes, welcomeFooterRoutes } from './routes/footer.routes';

const notFoundPage = {
  path: '**',
  loadComponent: () => import('@app/not-found-page').then(({ NotFoundPageComponent }) => NotFoundPageComponent),
};

const welcomePage = {
  path: '',
  loadComponent: () => import('@app/gofast/layout-welcome').then(({ LayoutWelcomeContentComponent }) => LayoutWelcomeContentComponent),
};

export const routes: Routes = [
  // Языковые welcome маршруты
  ...(Object.keys(LanguageService.getRouteLanguages())
    .filter((item) => item !== MAIN_LANG)
    .map((lang) => ({
      loadComponent: () => import('@app/gofast/layout-welcome').then(({ LayoutWelcomeComponent }) => LayoutWelcomeComponent),
      canActivate: [WelcomeWorkActivateGuard],
      providers: [MainModalsService],
      data: { seo: 'WELCOME' },
      path: `${lang}/${APP_PATHS.welcome}`,
      children: [welcomePage, ...welcomeFooterRoutes, notFoundPage],
    })) as Route[]),
  // Welcome маршрут
  {
    path: APP_PATHS.welcome,
    loadComponent: () => import('@app/gofast/layout-welcome').then(({ LayoutWelcomeComponent }) => LayoutWelcomeComponent),
    canActivate: [WelcomeWorkActivateGuard],
    providers: [MainModalsService],
    data: { seo: 'WELCOME' },
    children: [welcomePage, ...welcomeFooterRoutes, notFoundPage],
  },
  // Основное приложение на корневом маршруте
  {
    path: APP_PATHS.root,
    component: GofastLayoutComponent,
    canActivate: [WelcomeWorkDeactivateGuard],
    providers: [
      MainModalsService,
      RecentActivitiesService,
      {
        provide: RECENT_ACTIVITIES_ENGINE,
        useClass: RecentActivitiesService,
      },

      {
        provide: PARTICIPATION_PANEL_ENGINE,
        useClass: ParticipationPanelService,
      },

      {
        provide: PROMOCODE_ENGINE,
        useClass: PromocodeService,
      },
      {
        provide: REFILL_BANNER_ENGINE,
        useClass: RefillBannerService,
      },
      {
        provide: CHAT_ENGINE,
        useClass: ChatService,
      },
      {
        provide: INVENTORY_TOOLTIP_ENGINE,
        useClass: InventoryEngineService,
      },
      {
        provide: HAS_PERMISSION_ENGINE,
        useClass: HasPermissionService,
      },
    ],

    children: [
      {
        path: '',
        children: appRoutes,
      },
      {
        path: 'transaction',
        outlet: 'modal',
        loadChildren: () => import('@app/modules/transaction/transaction.fast.routes').then(({ routes: routesFast }) => routesFast),
        canActivate: [IsAuthGuard],
        canActivateChild: [AutoGameActivateChildGuard],
      },
      {
        path: 'balance',
        outlet: 'modal',
        canActivate: [IsAuthGuard],
        loadComponent: () =>
          import('@app/modules/account/modals/balance-modal/balance-modal.component').then(
            ({ BalanceModalComponent }) => BalanceModalComponent,
          ),
      },
      {
        path: 'fail-payment',
        outlet: 'modal',
        canActivate: [IsAuthGuard],

        children: [
          {
            path: ':id',
            loadComponent: () =>
              import('@app/gofast/modals-notification-layout').then(
                ({ ModalsNotificationLayoutComponent }) => ModalsNotificationLayoutComponent,
              ),

            canActivate: [IsAuthGuard],
          },
        ],

        data: { paymentStatus: TransactionStatus.ERRORED },
      },
      {
        path: 'success-payment',
        outlet: 'modal',
        canActivate: [IsAuthGuard],

        children: [
          {
            path: ':id',
            loadComponent: () =>
              import('@app/gofast/modals-notification-layout').then(
                ({ ModalsNotificationLayoutComponent }) => ModalsNotificationLayoutComponent,
              ),
            canActivate: [IsAuthGuard],
          },
        ],
        data: { paymentStatus: TransactionStatus.RECEIVED },
      },
      ...footerRoutes,
      ...(Object.keys(LanguageService.getRouteLanguages())
        .filter((item) => item !== MAIN_LANG)
        .map((lang) => ({
          path: lang,
          children: [...appRoutes, ...footerRoutes, notFoundPage],
        })) as Route[]),
      notFoundPage,
    ],
  },
];
